import { Box, Grid, Paper, Typography, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import ImsPaperHead from "../atoms/ImsPaperHead";
import { useEffect, useState, useCallback } from "react";
import { AnalysisValueDto } from "../../store/models/segments/AnalysisValue";
import { formatEuro, formatNumber } from "../atoms/Utils";
import { KNISTR_GREEN, KNISTR_RED } from "../atoms/ImsMaterialTheme";
import { GrayLine } from "../atoms/GrayLine";
import { CalendarMonth, TrendingDown, TrendingUp } from "@mui/icons-material";
import { Api } from "../../api/Api";

const formatCLVTrend = (value?: number) => {
  if (value === undefined || value === null) return null;

  return (
    <Box
      component="span"
      sx={{
        color: value < 0 ? KNISTR_RED : KNISTR_GREEN,
        display: "flex",
        alignItems: "center",
        gap: 0.5,
      }}
    >
      {value < 0 ? "-" : "+"} {formatNumber(Math.abs(value))}%
      {value < 0 ? (
        <TrendingDown sx={{ color: KNISTR_RED, fontSize: 42 }} />
      ) : (
        <TrendingUp sx={{ color: KNISTR_GREEN, fontSize: 42 }} />
      )}
    </Box>
  );
};

const CustomerAnalysis = ({ externalCustomerId }: { externalCustomerId: string }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const [analysisValue, setAnalysisValue] = useState<AnalysisValueDto>();

  const fetchCustomerAnalysis = useCallback(() => {
    Api.getPredictionValuesForCustomer(externalCustomerId)
      .then((data) => {
        setAnalysisValue(data);
      })
      .catch(() => {
        setAnalysisValue(undefined);
      });
  }, [externalCustomerId]);

  useEffect(() => {
    fetchCustomerAnalysis();
  }, [fetchCustomerAnalysis]);

  return (
    <Grid height={"100%"}>
      <Paper
        style={{
          padding: theme.spacing(3),
          height: "100%",
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
        }}
      >
        <div>
          <ImsPaperHead text={t("customerAnalysis.title")} />
        </div>
        <Grid container spacing={2}>
          <Grid item xs={12} container spacing={2} alignItems="center" justifyContent="center">
            <Grid item xs={4}>
              <b>{t("customerAnalysis.currentValue")}</b>
            </Grid>
            <Grid item xs={4}>
              <b>{t("customerAnalysis.predictedValue")}</b>
            </Grid>
            <Grid item xs={4}>
              <b>{t("customerAnalysis.clvTrend")}</b>
            </Grid>
          </Grid>
          <Grid item xs={12} container spacing={2} alignItems="center">
            <Grid item xs={4}>
              <Typography fontSize={42}>{formatEuro(analysisValue?.historicalClv ?? 0)}</Typography>
            </Grid>
            <Grid item xs={4}>
              <Typography fontSize={42}>{formatEuro(analysisValue?.predictedClv ?? 0)}</Typography>
            </Grid>
            <Grid item xs={4}>
              <Typography fontSize={42}>{formatCLVTrend(analysisValue?.clvChange)}</Typography>
            </Grid>
          </Grid>
          <Grid item xs={12}>
            <GrayLine />
          </Grid>
          <Grid item xs={12} container alignItems="center">
            <Grid item xs={6}>
              <b>{t("customerAnalysis.predictionTimeline")}</b>
            </Grid>
            <Grid item xs={6}></Grid>
          </Grid>
          <Grid item xs={12} container>
            <Grid item container xs={6}>
              <Grid item xs={6}>
                <Box display="flex" alignItems="center">
                  <CalendarMonth />
                  <b>{t("customerAnalysis.nextPurchaseDate")}</b>
                </Box>
              </Grid>
              <Grid item xs={6} textAlign="right" paddingRight={5}>
                {analysisValue?.predictedNextPurchaseDate}
              </Grid>
            </Grid>
            <Grid item xs={6}></Grid>
          </Grid>
        </Grid>
      </Paper>
    </Grid>
  );
};

export default CustomerAnalysis;
